/**
 * Performance Metrics — Car Alert BR
 *
 * Collects Core Web Vitals and logs them. In production these can be forwarded
 * to an analytics endpoint (Sentry, LogRocket, custom /api/metrics, etc.).
 *
 * Metrics collected:
 *   LCP  — Largest Contentful Paint  (target: < 2.5s)
 *   INP  — Interaction to Next Paint  (target: < 200ms)  [replaces FID in v3+]
 *   CLS  — Cumulative Layout Shift    (target: < 0.1)
 *   FCP  — First Contentful Paint     (target: < 1.8s)
 *   TTFB — Time to First Byte         (target: < 800ms)
 *
 * Usage:
 *   import { initPerformanceMetrics } from './utils/performanceMetrics';
 *   initPerformanceMetrics();   // call once at app bootstrap
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

/** Thresholds from https://web.dev/vitals/ */
const THRESHOLDS = {
  lcp:  { good: 2500, poor: 4000, unit: 'ms' },
  inp:  { good: 200,  poor: 500,  unit: 'ms' },
  cls:  { good: 0.1,  poor: 0.25, unit: '' },
  fcp:  { good: 1800, poor: 3000, unit: 'ms' },
  ttfb: { good: 800,  poor: 1800, unit: 'ms' },
};

/**
 * Classify a metric value against its thresholds.
 * @param {string} name - lowercase metric name (e.g. 'lcp')
 * @param {number} value
 * @returns {'good'|'needs-improvement'|'poor'}
 */
function classify(name, value) {
  const t = THRESHOLDS[name];
  if (!t) return 'unknown';
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Log a metric to the console and optionally send it to an analytics endpoint.
 * @param {{ name: string, value: number, id: string }} metric
 */
function handleMetric(metric) {
  const name = metric.name.toLowerCase();
  const rating = classify(name, metric.value);
  const threshold = THRESHOLDS[name];
  const display = threshold
    ? `${metric.value.toFixed(threshold.unit === 'ms' ? 0 : 3)}${threshold.unit}`
    : metric.value;

  if (process.env.NODE_ENV === 'development') {
    const badge =
      rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌';
    console.log(
      `[Web Vitals] ${badge} ${metric.name}: ${display} (${rating})`,
    );
  }

  // --- Optional: forward to your analytics backend ---
  // sendToAnalytics({ name: metric.name, value: metric.value, rating, id: metric.id });
}

/**
 * Initialise Core Web Vitals collection.
 * Call this once at app startup (e.g. from index.js after ReactDOM.render).
 */
export function initPerformanceMetrics() {
  onLCP(handleMetric);
  onINP(handleMetric);
  onCLS(handleMetric);
  onFCP(handleMetric);
  onTTFB(handleMetric);
}

export default initPerformanceMetrics;
